import React, {
  useRef, useEffect, useState, memo,
} from 'react';
import PropTypes from 'prop-types';
import clsx from 'clsx';

const ComputePositionComponent = ({ children, parentElementRef, reCalculatePosition }) => {
  const [position, setPosition] = useState({ top: 0, left: 0 });
  const ref = useRef(null);
  // Keep popper element hidden to avoid showing element at any intermediate position
  const visibility = useRef('hidden');

  const positionClass = useRef({});

  const handleScroll = () => {
    if (parentElementRef.current && ref.current) {
      const rect = parentElementRef.current.getBoundingClientRect();
      const { top, left } = rect;
      const windowHeight = window.innerHeight;
      const windowWidth = window.innerWidth;

      const currentElementRect = ref.current.getBoundingClientRect();

      // Ensure the element is within the viewport vertically
      const newPosition = {};
      if (top < 0) {
        newPosition.top = 0;
      } else if (top + currentElementRect.height > windowHeight) {
        newPosition.top = top - currentElementRect.height;
      } else {
        newPosition.top = top + rect.height;
      }

      // Ensure the element is within the viewport horizontally
      if (left < 0) {
        newPosition.left = 0;
      } else if (left + currentElementRect.width > windowWidth) {
        newPosition.left = rect.right - currentElementRect.width;
        positionClass.current = { left: true };
      } else {
        newPosition.left = left;
        positionClass.current = { right: true };
      }

      setPosition(newPosition);
    }
    if (visibility.current === 'hidden') {
      visibility.current = 'visible';
    }
  };

  useEffect(() => {
    window.addEventListener('scroll', handleScroll, true);
    window.addEventListener('resize', handleScroll, true);

    return () => {
      window.removeEventListener('scroll', handleScroll);
      window.removeEventListener('resize', handleScroll);
    };
  }, []);

  useEffect(() => {
    handleScroll();
  }, [reCalculatePosition]);

  const classes = clsx('cpc-wrapper', {
    left: positionClass.current.left,
    right: positionClass.current.right,
  });

  return (
    <div
      ref={ref}
      style={{
        zIndex: 999,
        visibility: visibility.current,
        position: 'fixed',
        top: position.top,
        left: position.left,
        bottom: position.bottom,
        right: position.right,
      }}
      className={classes}
    >
      {children}
    </div>
  );
};

ComputePositionComponent.propTypes = {
  children: PropTypes.element.isRequired,
  parentElementRef: PropTypes.oneOfType([
    PropTypes.func,
    PropTypes.shape({ current: PropTypes.instanceOf(Element) }),
  ]).isRequired,
  reCalculatePosition: PropTypes.oneOfType([PropTypes.bool, PropTypes.number]),
};

ComputePositionComponent.defaultProps = {
  reCalculatePosition: false,
};

export default memo(ComputePositionComponent);
